import * as React from 'react'

import { clsx } from 'clsx'

import { Badge, Button, Combobox, Text } from '@buffer-mono/popcorn'
import type {
  BadgeProps,
  ComboboxContentProps,
  ComboboxItemProps,
  ComboboxProps,
  ComboboxTriggerProps,
} from '@buffer-mono/popcorn'

import { useTemplateTracking } from '../../hooks/useTemplateTracking'

import styles from './TemplatesFilterCombobox.module.css'

import type { JSX } from 'react'

// --- Contexts ---

type FilterType = 'category' | 'madeFor' | 'format' | 'goal' | 'campaign'

type RootContextValue = {
  count: number
  registerItem: (value: string, filterType: FilterType) => void
  unregisterItem: (value: string) => void
  onReset: () => void
}

const RootContext = React.createContext<RootContextValue | null>(null)

const useRootContext = (): RootContextValue => {
  const ctx = React.useContext(RootContext)
  if (!ctx) {
    throw new Error(
      'TemplatesFilterCombobox compound components must be used within <TemplatesFilterCombobox>',
    )
  }
  return ctx
}

const GroupContext = React.createContext<FilterType | null>(null)

// --- Root ---

type RootProps = Omit<
  Extract<ComboboxProps, { multiple?: true }>,
  'multiple'
> & {
  cta: CTA
  onReset?: () => void
}

function Root({
  cta,
  value = [],
  onReset,
  onChange,
  children,
  ...props
}: RootProps): JSX.Element {
  const { trackTemplateFiltered } = useTemplateTracking()
  const filterTypeMapRef = React.useRef<Map<string, FilterType>>(new Map())

  const registerItem = React.useCallback(
    (itemValue: string, filterType: FilterType) => {
      filterTypeMapRef.current.set(itemValue, filterType)
    },
    [],
  )

  const unregisterItem = React.useCallback((itemValue: string) => {
    filterTypeMapRef.current.delete(itemValue)
  }, [])

  const handleChange = React.useCallback(
    (newValue: string[]) => {
      const addedFilters = newValue.filter((item) => !value.includes(item))

      addedFilters.forEach((filter) => {
        const filterType = filterTypeMapRef.current.get(filter) ?? 'madeFor'
        trackTemplateFiltered({ filterType, filterValue: filter, cta })
      })

      onChange?.(newValue)
    },
    [value, onChange, trackTemplateFiltered, cta],
  )

  const handleReset = React.useCallback(() => {
    if (value.length > 0) {
      trackTemplateFiltered({
        filterType: 'category',
        filterValue: 'reset',
        cta,
      })
    }
    onReset?.()
  }, [value, onReset, trackTemplateFiltered, cta])

  const contextValue = React.useMemo(
    () => ({
      count: value.length,
      registerItem,
      unregisterItem,
      onReset: handleReset,
    }),
    [value.length, registerItem, unregisterItem, handleReset],
  )

  return (
    <RootContext.Provider value={contextValue}>
      <Combobox multiple value={value} onChange={handleChange} {...props}>
        {children}
      </Combobox>
    </RootContext.Provider>
  )
}

Root.displayName = 'TemplatesFilterCombobox'

// --- Trigger ---

type TriggerElement = React.ElementRef<typeof Combobox.Trigger>
type TriggerProps = ComboboxTriggerProps

const Trigger = React.forwardRef<TriggerElement, TriggerProps>((props, ref) => (
  <Combobox.Trigger ref={ref} {...props} />
))

Trigger.displayName = 'TemplatesFilterCombobox.Trigger'

// --- Content ---

type ContentProps = ComboboxContentProps

function Content({ className, children, ...props }: ContentProps): JSX.Element {
  return (
    <Combobox.Content className={clsx(styles.content, className)} {...props}>
      {children}
    </Combobox.Content>
  )
}

Content.displayName = 'TemplatesFilterCombobox.Content'

// --- List ---

type ListProps = React.ComponentPropsWithoutRef<typeof Combobox.List>

const List = Combobox.List

// --- Group ---

type GroupProps = React.ComponentPropsWithoutRef<typeof Combobox.Group> & {
  filterType: FilterType
}

function Group({ filterType, children, ...props }: GroupProps): JSX.Element {
  return (
    <GroupContext.Provider value={filterType}>
      <Combobox.Group {...props}>{children}</Combobox.Group>
    </GroupContext.Provider>
  )
}

Group.displayName = 'TemplatesFilterCombobox.Group'

// --- Item ---

type ItemProps = ComboboxItemProps

function Item({ value, children, ...props }: ItemProps): JSX.Element {
  const { registerItem, unregisterItem } = useRootContext()
  const filterType = React.useContext(GroupContext)

  React.useEffect(() => {
    if (value && filterType) {
      registerItem(value, filterType)
      return (): void => unregisterItem(value)
    }
  }, [value, filterType, registerItem, unregisterItem])

  return (
    <Combobox.Item value={value} {...props}>
      <Text weight="medium">{children}</Text>
    </Combobox.Item>
  )
}

Item.displayName = 'TemplatesFilterCombobox.Item'

// --- Separator ---

type SeparatorProps = React.ComponentPropsWithoutRef<typeof Combobox.Separator>

const Separator = Combobox.Separator

// --- Counter ---

type CounterProps = Omit<BadgeProps, 'variant' | 'size' | 'children'>

function Counter(props: CounterProps): JSX.Element | null {
  const { count } = useRootContext()
  if (count === 0) return null
  return (
    <Badge variant="brand" size="small" {...props}>
      {count}
    </Badge>
  )
}

Counter.displayName = 'TemplatesFilterCombobox.Counter'

// --- Footer ---

type FooterProps = React.ComponentPropsWithoutRef<typeof Combobox.Footer>

const Footer = Combobox.Footer

// --- ResetButton ---

type ResetButtonProps = Omit<
  React.ComponentPropsWithoutRef<typeof Button>,
  'onClick' | 'variant'
>

const ResetButton = React.forwardRef<HTMLButtonElement, ResetButtonProps>(
  ({ className, children, ...props }, ref) => {
    const { onReset } = useRootContext()

    return (
      <Button
        ref={ref}
        variant="tertiary"
        className={clsx(styles.resetButton, className)}
        onClick={onReset}
        {...props}
      >
        {children}
      </Button>
    )
  },
)

ResetButton.displayName = 'TemplatesFilterCombobox.ResetButton'

// --- Export ---

export const TemplatesFilterCombobox = Object.assign(Root, {
  Trigger,
  Content,
  List,
  Group,
  Item,
  Separator,
  Counter,
  Footer,
  ResetButton,
})

export type {
  RootProps as TemplatesFilterComboboxProps,
  TriggerProps as TemplatesFilterComboboxTriggerProps,
  ContentProps as TemplatesFilterComboboxContentProps,
  ListProps as TemplatesFilterComboboxListProps,
  GroupProps as TemplatesFilterComboboxGroupProps,
  ItemProps as TemplatesFilterComboboxItemProps,
  SeparatorProps as TemplatesFilterComboboxSeparatorProps,
  CounterProps as TemplatesFilterComboboxCounterProps,
  FooterProps as TemplatesFilterComboboxFooterProps,
  ResetButtonProps as TemplatesFilterComboboxResetButtonProps,
}
